<script>
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { editorState, filterState } from "@app/store/editorStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import * as m from '@app/paraglide/messages.js';
import { decode } from "html-entities";
import QuillEditor from "./QuillEditor.svelte";
import EventEditorPreview from "./EventEditorPreview.svelte";
import TcInput from "./inputs/TcInput.svelte";
import CaptionStyle from "./inputs/CaptionStyle.svelte";
import EventPositionInput from "./inputs/EventPositionInput.svelte";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import sccLib from "@cclib/dict/608.js";
import insertEv from "@cclib/functions/eventGroups/insertEvent.js";
import removeEvent from "@cclib/functions/eventGroups/removeEvent.js";

// Import the utility functions from styleGuideTests.js
import {
    getTotalChars,
    calculateCps,
    calculateWpm,
    calculateDuration,
} from "@cclib/lib/styleGuideTests.js";
import { getFilteredEventIndices } from "@app/lib/eventValidation.js";
import { metricsState } from "@app/store/metricsStore.svelte.js";

import RealTimeErrors from "./RealTimeErrors.svelte";
import EventNotes from "./EventNotes.svelte";
import EventApproval from "./EventApproval.svelte";
import ForcedSubtitleEvent from "./ForcedSubtitleEvent.svelte";
import EventMetrics from "./EventMetrics.svelte";
import EventSpeakers from "./EventSpeakers.svelte";
import EventRecipients from "./EventRecipients.svelte";
import EventTags from "./EventTags.svelte";
import { tick } from "svelte";

/** @type {{eventIndex: number}} */
let { eventIndex } = $props();
let event = $derived(
    eventGroupState[projectState.selected]?.events[eventIndex]
);

let plainText = $derived(convertToPlainText(event?.text || ""));

let lineInfo = $derived.by(() => {
    let lines = [];
    plainText.split("\n").forEach((line) => {
        lines.push(decode(line).length);
    });

    return lines;
});

let totalWords = $derived(plainText.split(/\s/gm).length);

let totalChars = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return 0;
    // Use the imported getTotalChars function
    return getTotalChars(plainText, "text");
});

let cpsChars = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return 0;
    // Reading rate (CPS) can optionally exclude spaces based on user metrics settings
    return getTotalChars(plainText, "text", metricsState.cpsIncludeSpaces === false);
});

let duration = $derived(calculateDuration(event.start, event.end, 3));
let wpm = $derived(calculateWpm(totalWords, duration));
let cps = $derived(calculateCps(cpsChars, duration));

let overlaps = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return null;
    if (event.start === false || event.end === false) return null;
    if (
        event.start <
            eventGroupState[projectState.selected]?.events[eventIndex - 1]
                ?.end ||
        event.end >
            eventGroupState[projectState.selected]?.events[eventIndex + 1]
                ?.start
    ) {
        return true;
    }

    return false;
});

let illegalChars = $derived.by(() => {
    return plainText.split("").find((char) => {
        return sccLib.valid.indexOf(char) === -1;
    });
});

let errorMsg = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return null;
    let messages = [];

    if (overlaps && eventGroupState[projectState.selected]?.overlap) {
        messages.push("• Event overlap detected.");
    }

    if (eventGroupState[projectState.selected]?.illegalChars && illegalChars) {
        messages.push(
            `• Illegal non-608 compliant character found ${illegalChars}`,
        );
    }

    if (
        lineInfo.some((line) => {
            return line > eventGroupState[projectState.selected]?.maxChars;
        })
    ) {
        messages.push(
            `• Event exceeds maximum character count of ${eventGroupState[projectState.selected]?.maxChars}.`,
        );
    }

    if (lineInfo.length > eventGroupState[projectState.selected]?.maxLines) {
        messages.push(
            `• Event exceeds maximum line count of ${eventGroupState[projectState.selected]?.maxLines}.`,
        );
    }

    if (event.start !== false && event.end !== false) {
        if (duration < eventGroupState[projectState.selected]?.minDuration) {
            messages.push(
                `• Event is less than the minimum duration of ${eventGroupState[projectState.selected]?.minDuration} seconds.`,
            );
        } else if (
            duration > eventGroupState[projectState.selected]?.maxDuration
        ) {
            messages.push(
                `• Event exceeds maximum duration of ${eventGroupState[projectState.selected]?.maxDuration} seconds.`,
            );
        } else if (cps > eventGroupState[projectState.selected]?.maxCps) {
            messages.push(
                `• Event exceeds maximum CPS (${eventGroupState[projectState.selected]?.maxCps}).`,
            );
        } else if (wpm > eventGroupState[projectState.selected]?.maxWpm) {
            messages.push(
                `• Event exceeds maximum WPM (${eventGroupState[projectState.selected]?.maxWpm}).`,
            );
        }
    }

    return messages.join("\n");
});

function selectEvent(clickEvent, index) {
    try {
        if (!eventGroupState[projectState.selected]) return;

        if (clickEvent.ctrlKey || clickEvent.metaKey) {
            // Toggle selection with Ctrl/Cmd+Click
            if (
                eventGroupState[projectState.selected]?.selected.indexOf(
                    index,
                ) > -1
            ) {
                eventGroupState[projectState.selected].selected =
                    eventGroupState[projectState.selected]?.selected.filter(
                        (event) => {
                            return event !== index;
                        },
                    );
            } else {
                eventGroupState[projectState.selected].selected = [
                    ...eventGroupState[projectState.selected]?.selected,
                    index,
                ];
            }
        } else if (
            clickEvent.shiftKey &&
            eventGroupState[projectState.selected]?.selected.length > 0
        ) {
            // Range selection with Shift+Click - only select visible/filtered events
            const startIndex = eventGroupState[projectState.selected]?.selected[0];
            const endIndex = index;
            const minIndex = Math.min(startIndex, endIndex);
            const maxIndex = Math.max(startIndex, endIndex);
            
            // Get all filtered/visible event indices
            const events = eventGroupState[projectState.selected].events;
            const eventGroup = eventGroupState[projectState.selected];
            const filteredIndices = getFilteredEventIndices(events, eventGroup, filterState);
            
            // Filter to only include indices within the selection range
            const selectedIndices = filteredIndices.filter(i => i >= minIndex && i <= maxIndex);
            eventGroupState[projectState.selected].selected = selectedIndices;
        } else {
            // Normal selection
            eventGroupState[projectState.selected].selected = [index];

            if (
                lockState.video &&
                eventGroupState[projectState.selected]?.events[index].start &&
                !isNaN(
                    eventGroupState[projectState.selected]?.events[index].start,
                )
            ) {
                player.currentTime =
                    eventGroupState[projectState.selected]?.events[index].start;
            }

            if (eventGroupState[projectState.selected]?.selected[0] >= 0) {
                window.dispatchEvent(
                    new CustomEvent("scrollToVirtualListItem", {
                        detail: { index: index },
                    }),
                );
            }
        }
    } catch (err) {
        console.log("There was an error selecting the event. " + err.message);
        console.log(err);
    }
}

function insertEvent() {
    try {
        if (!eventGroupState[projectState.selected]) {
            return;
        }

        let eventOptions;
        if (eventGroupState[projectState.selected]?.events.length > 0) {
            try {
                let eventTemplate =
                    eventGroupState[projectState.selected]?.selected.length > 0
                        ? eventGroupState[projectState.selected]?.events[
                              eventGroupState[projectState.selected]
                                  ?.selected[0]
                          ]
                        : eventGroupState[projectState.selected]?.events[
                              eventGroupState[projectState.selected]?.events
                                  .length - 1
                          ];

                eventOptions = {
                    alignment: eventTemplate.alignment,
                    xPos: eventTemplate.xPos,
                    yPos: eventTemplate.yPos,
                    vertical: eventTemplate.vertical,
                    voice: eventTemplate.voice,
                    speakingStyle: eventTemplate.speakingStyle,
                    style: eventTemplate.style,
                    rate: eventTemplate.rate,
                    start: editorState.insertStartOnInsert
                        ? player.currentTime
                        : false,
                    end: editorState.insertStartOnInsert
                        ? player.currentTime +
                          eventGroupState[projectState.selected]?.minDuration
                        : false,
                };
            } catch (err) {
                console.log(err);
            }
        }

        // Insert new event
        eventGroupState[projectState.selected] = insertEv(
            eventGroupState[projectState.selected],
            eventOptions,
            eventIndex + 1,
        );
        setTimeout(function () {
            eventGroupState[projectState.selected].selected = [eventIndex + 1];
            const selectedIndex = eventIndex + 1;

            // Dispatch custom event to scroll to the newly inserted event
            window.dispatchEvent(
                new CustomEvent("scrollToVirtualListItem", {
                    detail: { index: selectedIndex },
                }),
            );
        }, 20);

        /* Store in History */
        insertHistoryAction({
            name: "insert event", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    } catch (err) {
        console.log(err, err.message);
    }
}

async function deleteEvent() {
    try {
        if (!eventGroupState[projectState.selected]) {
            return;
        }

        // Remove the event
        const eventIndexToDelete =  eventIndex;      
        // Update selection to previous event or clear if no events left
        let newSelectedIndex = eventIndexToDelete > 0 ? eventIndexToDelete - 1 : 0;
        if (!eventGroupState[projectState.selected].events[newSelectedIndex]) {
            eventGroupState[projectState.selected].selected = [];
        } else {
            eventGroupState[projectState.selected].selected = [newSelectedIndex];
            // Dispatch custom event to scroll to the selected event
            window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
                detail: { index: newSelectedIndex }
            }));
        }

        await tick();

        eventGroupState[projectState.selected] = removeEvent(eventGroupState[projectState.selected], eventIndexToDelete);

        /* Store in History */
        insertHistoryAction({
            name: "delete event", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    } catch (err) {
        console.log(err, err.message);
    }
}
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<form
    id="Event{eventIndex}"
    aria-label={m.event_subtitle_aria_label({eventIndex})}
    onclick={(clickEvent) => selectEvent(clickEvent, eventIndex)}
    class="rounded-0 p-3 eventItem {eventGroupState[
        projectState.selected
    ]?.selected.indexOf(eventIndex) > -1
        ? 'border border-warning bg-warning bg-opacity-25'
        :  eventGroupState[projectState.selected]?.events[eventIndex]?.forced ? 'bg-info bg-opacity-10' : ''}">
    <div class="row">
        <div class="col-2">
            <div class="btn-group float-end" role="group">
                <RealTimeErrors {errorMsg} />
                {#if event}
                    <ForcedSubtitleEvent bind:event></ForcedSubtitleEvent>
                    <EventApproval bind:event></EventApproval>
                    <EventNotes bind:event></EventNotes>
                {/if}
            </div>
            <p class="text-dark mb-1 fw-bold">{eventIndex + 1}</p>
            <div class="input-group input-group-sm mb-1">
                <TcInput bind:event tcType={"start"}></TcInput>
            </div>
            {#if !event?.style?.startsWith("Roll-Up")}
            <div class="input-group input-group-sm mb-2">
                <TcInput bind:event tcType={"end"}></TcInput>
            </div>
            {/if}
            <EventMetrics
                {wpm}
                {cps}
                {duration}
                {totalWords}
                {lineInfo}
                {totalChars}
                eventGroupSettings={{
                    maxChars: eventGroupState[projectState.selected]?.maxChars,
                    maxLines: eventGroupState[projectState.selected]?.maxLines,
                    maxCps: eventGroupState[projectState.selected]?.maxCps,
                    maxWpm: eventGroupState[projectState.selected]?.maxWpm,
                    minDuration:
                        eventGroupState[projectState.selected]?.minDuration,
                    maxDuration:
                        eventGroupState[projectState.selected]?.maxDuration,
                }}></EventMetrics>
        </div>
        <div class="col">
            {#if eventGroupState[projectState.selected]?.selected[0] === eventIndex}
                <QuillEditor bind:event bind:eventIndex></QuillEditor>
            {:else}
                <EventEditorPreview bind:event height={160}
                ></EventEditorPreview>
            {/if}
            <div class="row">
                <EventSpeakers bind:event></EventSpeakers>
                <EventRecipients bind:event></EventRecipients>
                <EventTags bind:event></EventTags>
            </div>
        </div>
        <div class="col-3">
            <CaptionStyle bind:event></CaptionStyle>
            <EventPositionInput bind:event></EventPositionInput>
        </div>
    </div>
    {#if eventGroupState[projectState.selected]?.selected[0] === eventIndex}
        <div class="event-action-buttons">
            <button
                id="insertSubtitleEventBtn-{eventIndex}"
                type="button"
                title={m.event_insert_button_title()}
                class="btn btn-primary btn-sm newEventButton"
                aria-label={m.event_insert_subtitle_aria_label()}
                onclick={() => insertEvent()}>
                <i class="bi bi-plus-lg"></i>
            </button>
            <button
                id="deleteSubtitleEventBtn-{eventIndex}"
                type="button"
                title={m.event_delete_button_title()}
                class="btn btn-danger btn-sm deleteEventButton"
                aria-label={m.event_delete_subtitle_aria_label()}
                onclick={() => deleteEvent()}>
                <i class="bi bi-trash"></i>
            </button>
        </div>
    {/if}
</form>

<style lang="css">
.event-action-buttons {
    position: absolute;
    bottom: -10px;
    left: 5%;
    transform: translateX(-0%);
    z-index: 1059;
    display: flex;
    gap: 5px;
}

.newEventButton, .deleteEventButton {
    box-shadow: 0.3rem 0.3rem 0.3rem rgb(0 0 0 / 50%);
    color: white;
}

.eventItem {
    min-height: 229px !important;
    position: relative;
}
</style>
